import { Contract, PlanfactOperation } from '../services/api';
import { sumContractFamilyReceiptsFromOperations } from './contractReceipts';

export interface ContractFamily {
  mainContract: Contract;
  familyContracts: Contract[];
  familyIds: Set<number>;
}

export function resolveContractFamily(
  seed: Contract,
  allContracts: Contract[],
): ContractFamily {
  const allById = new Map(allContracts.map((c) => [c.id, c]));
  let mainContractId = seed.id;

  if (seed.hierarchy_level === 2) {
    const main = allContracts.find((c) => (c.supplementary_contract_ids ?? []).includes(seed.id));
    if (main) {
      mainContractId = main.id;
    }
  }

  const mainContract = allById.get(mainContractId) ?? seed;
  const familyIds = new Set<number>([mainContract.id, ...(mainContract.supplementary_contract_ids ?? [])]);
  if (!familyIds.has(seed.id)) {
    familyIds.add(seed.id);
  }

  const familyContracts = [...familyIds]
    .map((id) => allById.get(id))
    .filter((c): c is Contract => c != null)
    .sort((a, b) => {
      const levelA = a.hierarchy_level ?? 1;
      const levelB = b.hierarchy_level ?? 1;
      if (levelA !== levelB) {
        return levelA - levelB;
      }
      if (levelA === 2) {
        const startA = a.start_date?.trim() ?? '';
        const startB = b.start_date?.trim() ?? '';
        if (startA && startB && startA !== startB) {
          return startA.localeCompare(startB);
        }
        if (startA && !startB) return -1;
        if (!startA && startB) return 1;
      }
      return a.contract_number.localeCompare(b.contract_number, 'ru', {
        numeric: true,
        sensitivity: 'base',
      });
    });

  return { mainContract, familyContracts, familyIds };
}

export function minFamilyDate(contracts: Contract[], field: 'start_date' | 'end_date'): string | null {
  let min: string | null = null;
  for (const c of contracts) {
    const raw = c[field];
    if (!raw?.trim()) continue;
    if (min == null || raw < min) {
      min = raw;
    }
  }
  return min;
}

export function maxFamilyDate(contracts: Contract[], field: 'start_date' | 'end_date'): string | null {
  let max: string | null = null;
  for (const c of contracts) {
    const raw = c[field];
    if (!raw?.trim()) continue;
    if (max == null || raw > max) {
      max = raw;
    }
  }
  return max;
}

export function sumFamilyAmount(contracts: Contract[]): number {
  return contracts.reduce((sum, c) => sum + (Number(c.total_amount) || 0), 0);
}

/** Уникальные семьи договоров контрагента (без повторов по главному договору). */
export function resolveContractorFamilies(
  contracts: Contract[],
): ContractFamily[] {
  const seenMainIds = new Set<number>();
  const families: ContractFamily[] = [];
  for (const contract of contracts) {
    const family = resolveContractFamily(contract, contracts);
    const mainId = family.mainContract.id;
    if (seenMainIds.has(mainId)) continue;
    seenMainIds.add(mainId);
    families.push(family);
  }
  return families;
}

/** Семьи контрагента по номеру главного договора; ДС сразу под своим договором 1-го уровня. */
export function sortContractsByFamilyHierarchy(contracts: Contract[]): Contract[] {
  const families = resolveContractorFamilies(contracts);
  families.sort((a, b) =>
    a.mainContract.contract_number.localeCompare(b.mainContract.contract_number, 'ru', {
      numeric: true,
    }),
  );
  return families.flatMap((family) => family.familyContracts);
}

export interface ContractorFinance {
  totalAmount: number;
  paymentPaid: number;
  paymentRemainder: number;
  paymentPercent: number;
}

/** Сумма договоров и поступлений контрагента с учётом иерархии (семьи договоров). */
export function resolveContractorFinance(params: {
  contracts: Contract[];
  planfactOperations?: PlanfactOperation[];
}): ContractorFinance {
  const { contracts, planfactOperations = [] } = params;
  const families = resolveContractorFamilies(contracts);
  const totalAmount = families.reduce(
    (sum, family) => sum + sumFamilyAmount(family.familyContracts),
    0,
  );

  let paymentPaid = 0;
  if (planfactOperations.length > 0) {
    paymentPaid = families.reduce(
      (sum, family) =>
        sum + sumContractFamilyReceiptsFromOperations(planfactOperations, family.familyIds),
      0,
    );
  } else {
    // payment_paid на договоре — агрегат по всей семье; берём один раз с главного договора.
    paymentPaid = families.reduce(
      (sum, family) => sum + (Number(family.mainContract.payment_paid) || 0),
      0,
    );
  }

  const paymentRemainder = totalAmount - paymentPaid;
  const paymentPercent = totalAmount > 0 ? Math.round((paymentPaid / totalAmount) * 100) : 0;

  return { totalAmount, paymentPaid, paymentRemainder, paymentPercent };
}
